import React from 'react';
import { ArrowLeft, Shield, Eye, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

export const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-purple-600 hover:text-purple-500 font-medium mb-4"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back to Home
          </Link>
          
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-6 py-2 mb-6 border border-purple-200">
              <Eye size={20} className="text-purple-600" />
              <span className="text-purple-700 font-medium">Privacy & Data</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          {/* Privacy Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-3">
              <Lock size={24} className="text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Your Privacy Matters</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  This Privacy Policy explains how Manivo collects, uses, and protects your information when you use our service. 
                  By using Manivo, you agree to the collection and use of information in accordance with this policy.
                </p>
              </div>
            </div>
          </div>

          <div className="prose prose-lg max-w-none space-y-8">
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">1.1 Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you create an account or use our Service, we may collect:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Email address and name</li>
                <li>Profile information you choose to provide</li>
                <li>Payment information (processed securely by third-party providers)</li>
                <li>Communication preferences</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">1.2 Usage Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We automatically collect information about how you use our Service:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Device information (type, operating system, browser)</li>
                <li>IP address and location data</li>
                <li>Pages visited, features used, and time spent</li>
                <li>Manifestation entries and progress data</li>
                <li>Search queries and interactions with our AI</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">1.3 Communication Data</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect and store:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Messages and conversations with our AI coach</li>
                <li>Support inquiries and feedback</li>
                <li>Survey responses and research participation</li>
                <li>Social media interactions related to our Service</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Service Provision</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Provide and maintain our manifestation coaching service</li>
                <li>Personalize your experience and recommendations</li>
                <li>Track your progress and generate insights</li>
                <li>Process payments and manage subscriptions</li>
                <li>Provide customer support</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Communication</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Send service-related notifications</li>
                <li>Provide updates about new features</li>
                <li>Send marketing communications (with consent)</li>
                <li>Respond to inquiries and support requests</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Analytics and Improvement</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Analyze usage patterns and user behavior</li>
                <li>Improve our Service and develop new features</li>
                <li>Conduct research and analytics</li>
                <li>Monitor and prevent fraud or misuse</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Information Sharing and Disclosure</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Third-Party Service Providers</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may share your information with trusted third parties who assist us in operating our Service:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Payment processors (Stripe, RevenueCat)</li>
                <li>Cloud storage and hosting providers</li>
                <li>Email service providers (Resend)</li>
                <li>Analytics and marketing platforms</li>
                <li>AI and machine learning service providers</li>
                <li>Customer support tools</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 Legal Requirements</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may disclose your information if required by law or to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Comply with legal processes or government requests</li>
                <li>Protect our rights, property, or safety</li>
                <li>Prevent fraud or illegal activities</li>
                <li>Enforce our Terms of Service</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">3.3 Business Transfers</h3>
              <p className="text-gray-700 leading-relaxed">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred 
                to the new entity as part of the business transaction.
              </p>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your information:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security audits and monitoring</li>
                <li>Access controls and authentication</li>
                <li>Secure development practices</li>
                <li>Employee training on data protection</li>
              </ul>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm">
                  <strong>Please note:</strong> While we strive to protect your information, no method of transmission 
                  over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We retain your information for as long as necessary to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Provide our Service to you</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Improve our Service and develop new features</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                When you delete your account, we will delete or anonymize your personal information within 
                30 days, except where we are required to retain it for legal or regulatory purposes.
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights and Choices</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 Access and Control</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Delete your account and data</li>
                <li>Port your data to another service</li>
                <li>Restrict processing of your information</li>
                <li>Object to certain uses of your information</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">6.2 Communication Preferences</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Opt out of marketing emails via unsubscribe links</li>
                <li>Manage notification settings in your account</li>
                <li>Contact us to update your preferences</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">6.3 Cookies and Tracking</h3>
              <p className="text-gray-700 leading-relaxed">
                You can control cookies through your browser settings. Note that disabling cookies may 
                affect the functionality of our Service.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Children's Privacy</h2>
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-red-800 font-semibold mb-2">Age Restriction</p>
                <p className="text-red-700 leading-relaxed">
                  Our Service is not intended for children under 18 years of age. We do not knowingly collect 
                  personal information from children under 18. If you are a parent or guardian and believe your 
                  child has provided personal information, please contact us immediately.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Your information may be transferred to and processed in countries other than your own. 
                These countries may have different data protection laws. We ensure appropriate safeguards 
                are in place to protect your information during such transfers.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Standard contractual clauses</li>
                <li>Adequacy decisions by relevant authorities</li>
                <li>Certification schemes and codes of conduct</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Third-Party Links and Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our Service may contain links to third-party websites or integrate with third-party services. 
                This Privacy Policy does not apply to such third parties. We recommend reviewing their privacy policies.
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-700 text-sm">
                  <strong>Third-party services we use may include:</strong> Stripe for payments, 
                  Supabase for authentication, ElevenLabs for voice services, and various analytics providers.
                </p>
              </div>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. California Privacy Rights (CCPA)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you are a California resident, you have additional rights under the California Consumer Privacy Act:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Right to know what personal information is collected</li>
                <li>Right to delete personal information</li>
                <li>Right to opt-out of the sale of personal information</li>
                <li>Right to non-discrimination for exercising privacy rights</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                <strong>Note:</strong> We do not sell personal information to third parties.
              </p>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. European Privacy Rights (GDPR)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you are in the European Economic Area, you have rights under the General Data Protection Regulation:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Lawful basis for processing (consent, contract, legitimate interests)</li>
                <li>Right to access, rectify, and erase personal data</li>
                <li>Right to restrict processing and data portability</li>
                <li>Right to object to processing</li>
                <li>Right to withdraw consent</li>
                <li>Right to lodge a complaint with a supervisory authority</li>
              </ul>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may update this Privacy Policy from time to time. When we do:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>We will post the updated policy on this page</li>
                <li>We will update the "Last updated" date</li>
                <li>For material changes, we will notify you via email or in-app notification</li>
                <li>Your continued use constitutes acceptance of the updated policy</li>
              </ul>
            </section>

            {/* Section 13 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Data Processing Activities</h2>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Summary of Processing</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-gray-700 mb-1">Data Controller:</p>
                    <p className="text-gray-600">Manivo Inc.</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700 mb-1">Processing Purpose:</p>
                    <p className="text-gray-600">Service provision, analytics, marketing</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700 mb-1">Legal Basis:</p>
                    <p className="text-gray-600">Consent, contract, legitimate interests</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700 mb-1">Data Categories:</p>
                    <p className="text-gray-600">Personal, usage, communication data</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                <p className="text-purple-700">
                  Email: privacy@manivo.com
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};